import Image from 'next/image';
import Link from 'next/link';

import { ArrowRight } from 'lucide-react';

import type { MarketingLocale } from '~/lib/marketing/site';
import { localizedHref } from '~/lib/marketing/routes';

import { cn } from '@kit/ui/utils';

import { LIGHT_LOGO_FILES, TRUST_BAR, TRUST_LOGOS } from '../../_lib/home.content';

const LOGO_BASE = '/landing-pages/flow/customers';

const CUSTOMERS_LINK = {
  en: 'Meet more of our customers',
  ar: 'تعرّف على المزيد من عملائنا',
} as const;

/**
 * Infinite client-logo strip. Items use trailing padding rather than `gap`
 * so the doubled track is exactly two equal halves and the loop never jumps.
 */
export function LogoMarquee({ locale }: { locale: MarketingLocale }) {
  const track = [...TRUST_LOGOS, ...TRUST_LOGOS];

  return (
    <section className="overflow-hidden bg-[#f7f6f4] py-12 md:py-14">
      <p className="mb-8 px-4 text-center font-roboto-mono text-[12px] uppercase tracking-wide text-[#8a857c] md:text-[13px]">
        {TRUST_BAR.heading[locale]}
      </p>

      <div
        dir="ltr"
        className="flow-marquee relative [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]"
      >
        <ul className="flow-marquee-track flex w-max items-center">
          {track.map((logo, index) => {
            const isCopy = index >= TRUST_LOGOS.length;

            return (
              <li
                key={`${logo.file}-${index}`}
                data-marquee-copy={isCopy}
                aria-hidden={isCopy || undefined}
                className="shrink-0 pr-12 md:pr-16"
              >
                <Image
                  src={`${LOGO_BASE}/${logo.file}`}
                  alt={isCopy ? '' : logo.name}
                  width={120}
                  height={56}
                  className={cn(
                    'h-10 w-auto object-contain opacity-55 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0 md:h-12',
                    LIGHT_LOGO_FILES.has(logo.file) && 'brightness-0 hover:brightness-100',
                  )}
                />
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-8 text-center">
        <Link
          href={localizedHref('/customers', locale)}
          className="group inline-flex items-center gap-2 font-roboto-mono text-[12px] uppercase tracking-wide text-[#202020]/70 transition-colors hover:text-[#202020]"
        >
          {CUSTOMERS_LINK[locale]}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
        </Link>
      </div>
    </section>
  );
}
